/**
 * Auth session cache so checkAuth can skip /auth/profile + /roles/:id,
 * and getAllPermissions can skip /permissions?paginate=0 within a TTL.
 * Uses localStorage so soft/hard reloads in the same browser keep the cache.
 */

const AUTH_CACHE_KEY = 'revivepay_auth_cache';
const PERMISSIONS_CATALOG_KEY = 'revivepay_permissions_catalog';
export const AUTH_SESSION_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

/** Short fingerprint of the JWT/token for cache key matching (not cryptographic). */
export function hashToken(token) {
  if (!token || typeof token !== 'string') return '';
  let hash = 0;
  for (let i = 0; i < token.length; i += 1) {
    hash = (hash << 5) - hash + token.charCodeAt(i);
    hash |= 0;
  }
  return String(hash);
}

function safeParse(raw) {
  try {
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function readStorage(key) {
  try {
    return safeParse(localStorage.getItem(key)) || safeParse(sessionStorage.getItem(key));
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  const raw = JSON.stringify(value);
  try {
    localStorage.setItem(key, raw);
  } catch {
    // ignore quota / private mode
  }
  try {
    sessionStorage.setItem(key, raw);
  } catch {
    // ignore
  }
}

function removeStorage(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function clearAuthSessionCache() {
  removeStorage(AUTH_CACHE_KEY);
  removeStorage(PERMISSIONS_CATALOG_KEY);
}

export function readAuthSessionCache() {
  return readStorage(AUTH_CACHE_KEY);
}

/** Keep only fields needed to hydrate auth UI (avoids quota / stringify failures). */
function slimUser(user) {
  if (!user || typeof user !== 'object') return null;
  return {
    id: user.id ?? null,
    uuid: user.uuid ?? null,
    name: user.name ?? null,
    email: user.email ?? null,
    username: user.username ?? null,
    phone: user.phone ?? null,
    role: user.role ?? null,
    roles: Array.isArray(user.roles) ? user.roles : undefined,
    impersonating: user.impersonating === true,
    avatar: user.avatar?.url
      ? { url: user.avatar.url }
      : typeof user.avatar === 'string'
        ? user.avatar
        : undefined,
    avatar_url: user.avatar_url ?? undefined,
  };
}

/**
 * @returns {null | { tokenHash: string, user: object, permissions: string[], roleId: *, cachedAt: number, impersonating?: boolean }}
 */
export function getFreshAuthSessionCache(token, ttlMs = AUTH_SESSION_CACHE_TTL_MS) {
  if (!token) return null;
  const cached = readAuthSessionCache();
  if (!cached) return null;
  if (cached.tokenHash !== hashToken(token)) return null;
  if (!cached.user || !Array.isArray(cached.permissions)) return null;
  const age = Date.now() - Number(cached.cachedAt || 0);
  if (!Number.isFinite(age) || age < 0 || age > ttlMs) return null;
  return cached;
}

export function writeAuthSessionCache({ token, user, permissions, roleId, impersonating }) {
  if (!token || !user) return;
  const slim = slimUser(user);
  if (!slim) return;
  writeStorage(AUTH_CACHE_KEY, {
    tokenHash: hashToken(token),
    user: slim,
    permissions: Array.isArray(permissions) ? permissions : [],
    roleId: roleId ?? null,
    impersonating: impersonating === true,
    cachedAt: Date.now(),
  });
}

/**
 * @returns {null | unknown} cached permissions catalog payload
 */
export function getFreshPermissionsCatalog(token, ttlMs = AUTH_SESSION_CACHE_TTL_MS) {
  if (!token) return null;
  const cached = readStorage(PERMISSIONS_CATALOG_KEY);
  if (!cached) return null;
  if (cached.tokenHash !== hashToken(token)) return null;
  if (cached.data == null) return null;
  const age = Date.now() - Number(cached.cachedAt || 0);
  if (!Number.isFinite(age) || age < 0 || age > ttlMs) return null;
  return cached.data;
}

export function writePermissionsCatalog(token, data) {
  if (!token || data == null) return;
  writeStorage(PERMISSIONS_CATALOG_KEY, {
    tokenHash: hashToken(token),
    data,
    cachedAt: Date.now(),
  });
}
